import { useContext, useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { Tab } from 'react-bootstrap'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { Tabs } from '../../../components/Tabs/Tabs'
import { IntegrationAboutTab } from './IntegrationAboutTab'
import { IntegrationManagementHeader } from './IntegrationManagementHeader'
import { IntegrationAdminSettingsContainer } from './AdminSettings/AdminSettings'
import { IntegrationAdminSettingsCustom } from './AdminSettings/IntegrationAdminSettingsCustom'
import { useZocksAuth, useZocksQuota } from '../../../components/ScopedBlock/hooks'
import { useHasIntegrationConnectScope } from '../../../components/Integrations/useHasIntegrationConnectScopeAndQuota'
import { HeaderContext } from '../../LayoutWithLeftSidebar/HeaderContext'
import { useAppSelector } from '../../../store/hooks'
import { selectAccountId } from '../../../store/auth/authSlice'
import { useGetMeQuery } from '../../../store/user/user'
import { useGetIntegrationAdminSettings } from './AdminSettings/hooks'
import { useIntegrationSpecificAdminSections } from './AdminSettings/IntegrationSpecificAdminSettings'
import { IntegrationServiceId } from '../../../common/integrations'
import { useIntegrationSpecificSettings } from './IntegrationSpecificSettings/IntegrationSpecificSetting'
import { ClickNavigation } from '../../../components/NavigationWithSideBar/ClickNavigation'

const SETTINGS_ALWAYS_ACCESSIBLE = new Set([IntegrationServiceId.DELEGATED_EMAIL])

// Integrations whose Admin tab renders lightweight sections directly, rather than the CRM mapping form.
const ADMIN_SECTIONS_WITHOUT_MAPPING_FORM = new Set([IntegrationServiceId.E_MONEY])

export const IntegrationManagementTabs: React.FC = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { portalRef } = useContext(HeaderContext)
  const { hasScope } = useZocksAuth()

  const { serviceId, activeTab = 'about' } = useParams<{
    serviceId: string
    activeTab?: string
  }>() as { serviceId: IntegrationServiceId; activeTab?: string }

  const accountId = useAppSelector(selectAccountId)
  const { data: userData, isLoading: isUserLoading } = useGetMeQuery({ accountId })
  const { currentIntegrationAccountConfig } = useGetIntegrationAdminSettings(serviceId)

  const hasIntegration = useMemo(() => {
    if (!userData || !userData.integrations) {
      return false
    }
    return !!userData.integrations.some((i) => i.serviceId === serviceId)
  }, [userData, serviceId])

  const hasIntegrationConnectScope = useHasIntegrationConnectScope(serviceId)
  const { isFetching: isQuotaFetching } = useZocksQuota()

  const hasAdminScope = hasIntegrationConnectScope && hasScope('**', 'account:update', ['account:admin'])

  const yourSettingsSections = useIntegrationSpecificSettings(serviceId)
  const showYourSettingsTab = yourSettingsSections.length > 0

  const adminSettingsSections = useIntegrationSpecificAdminSections(serviceId)
  const showAdminTab = !!(adminSettingsSections && adminSettingsSections.length > 0)

  const canAccessSettings = showYourSettingsTab && hasIntegrationConnectScope && (hasIntegration || SETTINGS_ALWAYS_ACCESSIBLE.has(serviceId))

  const canAccessAdmin = showAdminTab && hasAdminScope

  useEffect(() => {
    if (isUserLoading || isQuotaFetching) {
      return
    }

    const fallbackUrl = `/account/integration-settings/integrations/${serviceId}/about`

    if (activeTab === 'settings' && !canAccessSettings) {
      navigate(fallbackUrl, { replace: true })
    }

    if (activeTab === 'admin' && !canAccessAdmin) {
      navigate(fallbackUrl, { replace: true })
    }
  }, [activeTab, canAccessSettings, canAccessAdmin, isUserLoading, isQuotaFetching, serviceId, navigate])

  const handleTabSelect = (key: string | null) => {
    if (key && key !== activeTab) {
      navigate(
        {
          pathname: `/account/integration-settings/integrations/${serviceId}/${key}`,
          search: searchParams.toString(),
        },
        { replace: false },
      )
    }
  }

  const [headerContainer, setHeaderContainer] = useState<HTMLElement | null>(null)
  useEffect(() => {
    if (portalRef && portalRef.current) {
      setHeaderContainer(portalRef.current)
    }
  }, [portalRef])

  return (
    <>
      {headerContainer ? createPortal(<IntegrationManagementHeader backTarget={'integration'} />, headerContainer) : null}

      <Tabs className='mb-6' id='integration-management-tabs' defaultActiveKey={activeTab} activeKey={activeTab} onSelect={handleTabSelect}>
        <Tab eventKey='about' title='About'>
          <IntegrationAboutTab serviceId={serviceId} />
        </Tab>

        {showYourSettingsTab && (
          <Tab eventKey='settings' title='Your Settings' disabled={!canAccessSettings}>
            {activeTab === 'settings' && canAccessSettings && (
              <ClickNavigation sections={yourSettingsSections} defaultActiveSectionId={yourSettingsSections[0].id} />
            )}
          </Tab>
        )}

        {showAdminTab && (
          <Tab eventKey='admin' title='Admin' disabled={!hasAdminScope}>
            {activeTab === 'admin' &&
              canAccessAdmin &&
              (ADMIN_SECTIONS_WITHOUT_MAPPING_FORM.has(serviceId) ? (
                <ClickNavigation sections={adminSettingsSections} defaultActiveSectionId={adminSettingsSections[0].id} />
              ) : !currentIntegrationAccountConfig?.isCustom ? (
                <IntegrationAdminSettingsContainer sections={adminSettingsSections} serviceId={serviceId} />
              ) : (
                <IntegrationAdminSettingsCustom serviceId={serviceId} />
              ))}
          </Tab>
        )}
      </Tabs>
    </>
  )
}
